/**
 * VÉRAULT Portal — Content Studio · Light Content Calendar
 *
 * Monthly grid showing journal activity on each date:
 *   · Scheduled (gold dot, gold border on the date cell)
 *   · Published (sage dot)
 *   · Drafts last edited that day (neutral dot)
 *
 * Click a cell to filter and jump to entries on that day.
 * Click an entry chip to open the editor.
 *
 * No new endpoints — derives from /api/cms/journal.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon } from "lucide-react";
import { Surface, EditorialRule, Empty, Skeleton, PortalButton, api } from "../../../index";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function startOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 1));
}
function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}
function ymd(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function parseIsoDate(s) {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d;
}

export default function ContentCalendar() {
  const navigate = useNavigate();
  const today = new Date();
  const [cursor, setCursor] = React.useState({
    year: today.getUTCFullYear(),
    month: today.getUTCMonth(),
  });
  const [entries, setEntries] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [selectedDay, setSelectedDay] = React.useState(null);

  React.useEffect(() => {
    api
      .get("/cms/journal")
      .then(({ data }) => setEntries(Array.isArray(data) ? data : []))
      .catch(() => setError("Couldn't load the journal."));
  }, []);

  // Bucket entries by ymd for the current visible month
  const byDay = React.useMemo(() => {
    if (!entries) return {};
    const map = {};
    for (const e of entries) {
      let bucketKey = null;
      let kind = null;
      if (e.status === "scheduled" && e.scheduled_publish_at) {
        const d = parseIsoDate(e.scheduled_publish_at);
        if (d) {
          bucketKey = ymd(d);
          kind = "scheduled";
        }
      } else if (e.status === "published" && e.published_at) {
        const d = parseIsoDate(e.published_at);
        if (d) {
          bucketKey = ymd(d);
          kind = "published";
        }
      } else if (e.status === "draft" && e.updated_at) {
        const d = parseIsoDate(e.updated_at);
        if (d) {
          bucketKey = ymd(d);
          kind = "draft";
        }
      }
      if (!bucketKey) continue;
      if (!map[bucketKey]) map[bucketKey] = [];
      map[bucketKey].push({ ...e, _kind: kind });
    }
    return map;
  }, [entries]);

  const { year, month } = cursor;
  const first = startOfMonth(year, month);
  const startWeekday = first.getUTCDay(); // 0 = Sun
  const total = daysInMonth(year, month);
  const cells = [];
  // leading blanks
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  // trailing blanks to fill last week
  while (cells.length % 7 !== 0) cells.push(null);

  const goPrev = () => {
    setSelectedDay(null);
    setCursor((c) => {
      const m = c.month - 1;
      return m < 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: m };
    });
  };
  const goNext = () => {
    setSelectedDay(null);
    setCursor((c) => {
      const m = c.month + 1;
      return m > 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: m };
    });
  };
  const goToday = () => {
    setSelectedDay(null);
    setCursor({ year: today.getUTCFullYear(), month: today.getUTCMonth() });
  };

  const selectedEntries = selectedDay ? byDay[selectedDay] || [] : [];

  return (
    <div className="space-y-6" data-testid="content-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={goPrev}
            data-testid="content-calendar-prev"
            aria-label="Previous month"
            className="size-9 inline-flex items-center justify-center rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
          >
            <ChevronLeft className="size-4" strokeWidth={1.5} />
          </button>
          <div className="px-2">
            <div
              className="font-serif text-xl text-[hsl(var(--foreground))]"
              data-testid="content-calendar-month-label"
            >
              {MONTHS[month]} {year}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
              {Object.keys(byDay).filter((k) => k.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`)).reduce(
                (acc, k) => acc + byDay[k].length,
                0,
              )}{" "}
              entries this month
            </div>
          </div>
          <button
            onClick={goNext}
            data-testid="content-calendar-next"
            aria-label="Next month"
            className="size-9 inline-flex items-center justify-center rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
          >
            <ChevronRight className="size-4" strokeWidth={1.5} />
          </button>
          <button
            onClick={goToday}
            data-testid="content-calendar-today"
            className="ml-2 px-3 h-9 font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))] rounded-full transition-colors"
          >
            Today
          </button>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
            <span className="inline-flex items-center gap-1.5">
              <span className="size-1.5 rounded-full bg-[hsl(var(--accent-gold))]" /> Scheduled
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-1.5 rounded-full bg-[hsl(var(--accent-sage))]" /> Published
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-1.5 rounded-full bg-[hsl(var(--foreground-muted))]/60" /> Draft edited
            </span>
          </div>
          <PortalButton
            variant="primary"
            size="md"
            icon={<Plus className="size-3.5" strokeWidth={1.5} />}
            onClick={() => navigate("/advisor/content/journal/new")}
            data-testid="content-calendar-new-entry"
          >
            New entry
          </PortalButton>
        </div>
      </div>

      {error ? (
        <Empty title={error} icon={<CalendarIcon className="size-6" strokeWidth={1.25} />} />
      ) : !entries ? (
        <Skeleton className="h-[480px]" />
      ) : (
        <Surface variant="default" padded={false} className="overflow-hidden">
          {/* Weekday header */}
          <div className="grid grid-cols-7 border-b border-[rgba(0,0,0,0.06)]">
            {WEEKDAYS.map((w) => (
              <div
                key={w}
                className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]"
              >
                {w}
              </div>
            ))}
          </div>
          {/* Cells */}
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              if (d === null) {
                return (
                  <div
                    key={`b-${i}`}
                    className="min-h-[96px] sm:min-h-[112px] border-r border-b border-[rgba(0,0,0,0.04)] bg-[hsl(var(--background-paper))]/30"
                  />
                );
              }
              const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
              const list = byDay[key] || [];
              const isToday =
                d === today.getUTCDate() &&
                month === today.getUTCMonth() &&
                year === today.getUTCFullYear();
              const isSelected = selectedDay === key;
              const hasScheduled = list.some((e) => e._kind === "scheduled");
              return (
                <button
                  key={key}
                  onClick={() => setSelectedDay(isSelected ? null : key)}
                  data-testid={`content-calendar-day-${key}`}
                  className={`text-left min-h-[96px] sm:min-h-[112px] border-r border-b border-[rgba(0,0,0,0.04)] p-2 hover:bg-[hsl(var(--background-paper))]/60 transition-colors ${
                    isSelected ? "bg-[hsl(var(--background-paper))]" : ""
                  } ${hasScheduled ? "ring-1 ring-inset ring-[hsl(var(--accent-gold))]/30" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex items-center justify-center size-6 rounded-full font-mono text-[11px] tabular-nums ${
                        isToday
                          ? "bg-[hsl(var(--accent-gold))] text-[#1A1A1A] font-medium"
                          : "text-[hsl(var(--foreground))]"
                      }`}
                    >
                      {d}
                    </span>
                    {list.length > 0 && (
                      <div className="flex items-center gap-1">
                        {list.some((e) => e._kind === "scheduled") && (
                          <span className="size-1.5 rounded-full bg-[hsl(var(--accent-gold))]" />
                        )}
                        {list.some((e) => e._kind === "published") && (
                          <span className="size-1.5 rounded-full bg-[hsl(var(--accent-sage))]" />
                        )}
                        {list.some((e) => e._kind === "draft") && (
                          <span className="size-1.5 rounded-full bg-[hsl(var(--foreground-muted))]/60" />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="mt-2 space-y-1">
                    {list.slice(0, 2).map((e) => {
                      const dot =
                        e._kind === "scheduled"
                          ? "bg-[hsl(var(--accent-gold))]"
                          : e._kind === "published"
                          ? "bg-[hsl(var(--accent-sage))]"
                          : "bg-[hsl(var(--foreground-muted))]/60";
                      return (
                        <div
                          key={e.id}
                          className="flex items-center gap-1.5 font-body text-[11px] text-[hsl(var(--foreground-secondary))] truncate"
                        >
                          <span className={`size-1 rounded-full shrink-0 ${dot}`} />
                          <span className="truncate">{e.title || "Untitled"}</span>
                        </div>
                      );
                    })}
                    {list.length > 2 && (
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                        +{list.length - 2} more
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </Surface>
      )}

      {/* Selected day drawer */}
      {selectedDay && (
        <div data-testid="content-calendar-selection">
          <EditorialRule
            label={`${selectedEntries.length} entr${selectedEntries.length === 1 ? "y" : "ies"} on ${selectedDay}`}
          />
          <div className="mt-5">
            {selectedEntries.length === 0 ? (
              <Empty
                title="Nothing on this day."
                hint="Start a new entry and schedule it for this date."
                icon={<CalendarIcon className="size-5" strokeWidth={1.25} />}
              />
            ) : (
              <Surface variant="default" padded={false} className="overflow-hidden">
                <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                  {selectedEntries.map((e) => (
                    <li key={e.id}>
                      <button
                        onClick={() => navigate(`/advisor/content/journal/${e.id}`)}
                        className="w-full text-left grid grid-cols-[auto_1fr_auto] items-center gap-4 px-5 py-3 hover:bg-[hsl(var(--background-paper))]/60 transition-colors"
                        data-testid={`content-calendar-entry-${e.id}`}
                      >
                        <span
                          className={`size-2 rounded-full ${
                            e._kind === "scheduled"
                              ? "bg-[hsl(var(--accent-gold))]"
                              : e._kind === "published"
                              ? "bg-[hsl(var(--accent-sage))]"
                              : "bg-[hsl(var(--foreground-muted))]/60"
                          }`}
                        />
                        <div className="min-w-0">
                          <div className="font-serif text-[15px] text-[hsl(var(--foreground))] truncate">
                            {e.title || "Untitled"}
                          </div>
                          <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] truncate">
                            {[e.category, e.destination_tag].filter(Boolean).join(" · ") || "Uncategorized"}
                          </div>
                        </div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                          {e._kind}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </Surface>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
